import { TourStep } from 'core/tour';

import { DailyMetricCard } from './_components';

import { useCostOverTime } from '../../../cost-report/hooks/useCostOverTime';
import { useCostReportContext } from '../../../cost-report/hooks/useCostReportContext';
import { isStorageReportsSupported } from '../../utils';
import { Metric } from '../_components/metrics-card/Metric';
import { MetricsCard } from '../_components/metrics-card/MetricsCard';
import { costOverTimeTourId, tourSteps } from '../tourSteps';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { Box, Stack } from '@cast/design-system';

export const DailyMetricsSection = () => {
  const { cluster } = useCluster();
  const { isDefaultFilter, hasGpuData } = useCostReportContext();
  const showStorage = isStorageReportsSupported(cluster);
  const { aggregatedMetrics: data, dynamicCopiesMap } = useCostOverTime();

  return (
    <Stack gap={24} data-hc="success">
      <TourStep
        step={tourSteps.metricCards}
        type={costOverTimeTourId}
        text="View a summary of your cluster costs here and the monthly forecast."
        highlight="shadow"
        placement="top"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="stretch"
          gap={24}
        >
          <DailyMetricCard
            title={isDefaultFilter ? 'Current month spend' : 'Compute spend'}
            costStyle="large"
            metrics={{ cost: data.computeCost.rangeSoFar }}
            titleInfo={
              isDefaultFilter
                ? dynamicCopiesMap.summary.currentSpendTooltip
                : dynamicCopiesMap.summary.totalSpendTooltip
            }
          />
          <DailyMetricCard
            title={isDefaultFilter ? 'Monthly forecast' : 'Avg. monthly cost'}
            costStyle={hasGpuData ? 'medium' : 'small'}
            titleInfo={
              isDefaultFilter
                ? dynamicCopiesMap.summary.monthlyForecastTooltip
                : dynamicCopiesMap.summary.monthlyAverageTooltip
            }
            tendencyHint="Than last month"
            approx={isDefaultFilter}
            metrics={data.monthlyForecast}
          />
          <DailyMetricCard
            title="Avg. daily cost"
            costStyle={hasGpuData ? 'medium' : 'small'}
            metrics={data.avgDailyCost}
            tendencyHint="Than last month"
            titleInfo={
              isDefaultFilter
                ? 'Average daily compute cost'
                : 'Selected period average daily compute cost'
            }
          />
          <MetricsCard
            title="Avg. daily cost per resource"
            titleInfo="Average normalized cost per 1 unit of provisioned resource"
            metrics={
              <Stack gap={4}>
                <Metric
                  title="CPU"
                  value={data.avgDailyCostPerCpu}
                  unit="CPU"
                />
                <Metric
                  title="MEMORY"
                  value={data.avgDailyCostPerGib}
                  unit="GiB"
                />
                {hasGpuData && (
                  <Metric
                    title="GPU"
                    value={data.avgDailyCostPerGpu}
                    unit="GPU"
                  />
                )}
                {showStorage && (
                  <Metric
                    title="STORAGE"
                    value={data.avgDailyCostPerStorage}
                    unit="GiB"
                  />
                )}
              </Stack>
            }
            testId="cost-overview"
          />
        </Box>
      </TourStep>
    </Stack>
  );
};
